(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-miresmo-stock-pagination]';
  const PAGE_PARAM = 'page';
  const AVAILABILITY_PARAM = 'filter.v.availability';
  const SOLD_OUT_CLASS = 'miresmo-collection-card--sold-out';

  function int(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function createUrl(virtualPage) {
    const url = new URL(window.location.href);

    url.searchParams.delete(PAGE_PARAM);
    url.searchParams.delete('section_id');

    if (virtualPage > 1) {
      url.searchParams.set(PAGE_PARAM, String(virtualPage));
    }

    return url;
  }

  function getVisibleVirtualPage() {
    const params = new URL(window.location.href).searchParams;
    return Math.max(1, int(params.get(PAGE_PARAM), 1));
  }

  function hasExplicitAvailabilityFilter() {
    return new URL(window.location.href).searchParams.has(AVAILABILITY_PARAM);
  }

  function getPagesToDisplay(current, total) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const pages = [1];

    if (current > 4) pages.push('ellipsis-left');

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (current < total - 3) pages.push('ellipsis-right');
    pages.push(total);

    return pages;
  }

  function buildPageItem(page, current) {
    const li = document.createElement('li');

    if (typeof page !== 'number') {
      const span = document.createElement('span');
      span.className = 'hdt-pagination__item miresmo-stock-pagination__ellipsis';
      span.textContent = '…';
      span.setAttribute('aria-hidden', 'true');
      li.appendChild(span);
      return li;
    }

    if (page === current) {
      const span = document.createElement('span');
      span.className = 'hdt-pagination__item hdt-pagination__item--current';
      span.textContent = String(page);
      span.setAttribute('aria-current', 'page');
      li.appendChild(span);
      return li;
    }

    const link = document.createElement('a');
    link.className = 'hdt-pagination__item';
    link.href = createUrl(page).toString();
    link.dataset.miresmoVirtualPage = String(page);
    link.textContent = String(page);
    link.setAttribute('aria-label', `Mergi la pagina ${page}`);
    li.appendChild(link);

    return li;
  }

  function buildArrow(label, page, type) {
    const li = document.createElement('li');
    const link = document.createElement('a');

    link.className =
      `hdt-pagination__item hdt-pagination__item-arrow ` +
      (type === 'previous'
        ? 'hdt-pagination__item--next'
        : 'hdt-pagination__item--prev');

    link.href = createUrl(page).toString();
    link.dataset.miresmoVirtualPage = String(page);
    link.setAttribute('aria-label', label);

    const text = document.createElement('span');
    text.textContent = label;
    link.appendChild(text);

    li.appendChild(link);
    return li;
  }

  class MiresmoStockPager {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.miresmoSectionId;
      this.productsId = root.dataset.miresmoProductsId;
      this.pageSize = Math.max(1, int(root.dataset.miresmoPageSize, 24));
      this.inStockCount = Math.max(0, int(root.dataset.miresmoInStockCount, 0));
      this.outOfStockCount = Math.max(0, int(root.dataset.miresmoOutOfStockCount, 0));
      this.hasAvailabilityFilter = root.dataset.miresmoHasAvailabilityFilter === 'true';
      this.availabilityActive = root.dataset.miresmoAvailabilityActive === 'true';
      this.mount = root.querySelector('[data-miresmo-stock-pagination-mount]');
      this.grid = root.querySelector(`#${CSS.escape(this.productsId)}`);
      this.nativePagination = root.querySelector(
        '.hdt-pagination-wrapper.hdt-pagination-main-pages'
      );
      this.abortController = null;
      this.loading = false;
      this.aggregateCache = null;

      this.nativePages = Math.max(1, int(this.grid?.dataset.miresmoNativePages, 1));
      this.nativeItems = Math.max(0, int(this.grid?.dataset.miresmoNativeItems, 0));

      const availabilityTotal = this.inStockCount + this.outOfStockCount;
      this.useAvailabilityBackend =
        this.hasAvailabilityFilter && availabilityTotal === this.nativeItems;

      this.totalItems = this.useAvailabilityBackend
        ? availabilityTotal
        : this.nativeItems;

      this.totalPages = Math.ceil(this.totalItems / this.pageSize);
      this.currentPage = Math.min(
        Math.max(1, getVisibleVirtualPage()),
        Math.max(1, this.totalPages)
      );
    }

    shouldRun() {
      if (!this.root || !this.mount || !this.grid) return false;

      // If the shopper explicitly filters by availability, Shopify's own
      // filtered pagination is already correct and should stay native.
      if (this.availabilityActive || hasExplicitAvailabilityFilter()) {
        return false;
      }

      return this.totalItems > 0;
    }

    init() {
      if (!this.shouldRun()) {
        this.mount?.remove();
        return;
      }

      this.root.dataset.miresmoStockPagerReady = 'true';
      this.root.dataset.miresmoStockPagerMode = this.useAvailabilityBackend
        ? 'availability-filter'
        : 'aggregate-pages';

      if (this.nativePagination) {
        this.nativePagination.classList.add('miresmo-native-pagination-hidden');
      }

      if (this.currentPage !== getVisibleVirtualPage()) {
        window.history.replaceState({}, '', createUrl(this.currentPage));
      }

      this.renderPagination();
      this.loadPage(this.currentPage, { scroll: false, updateHistory: false });

      this.root.addEventListener('click', (event) => {
        const link = event.target.closest('[data-miresmo-virtual-page]');
        if (!link || !this.root.contains(link)) return;

        event.preventDefault();

        const targetPage = Math.min(
          Math.max(1, int(link.dataset.miresmoVirtualPage, 1)),
          Math.max(1, this.totalPages)
        );

        if (targetPage === this.currentPage && !this.loading) return;

        this.loadPage(targetPage, {
          scroll: true,
          updateHistory: true
        });
      });
    }

    getFetchUrl({ availability = null, sourcePage = 1 } = {}) {
      const url = new URL(window.location.href);

      url.searchParams.delete(PAGE_PARAM);
      url.searchParams.delete('section_id');
      url.searchParams.delete(AVAILABILITY_PARAM);

      if (availability !== null) {
        url.searchParams.set(AVAILABILITY_PARAM, availability);
      }

      if (sourcePage > 1) {
        url.searchParams.set(PAGE_PARAM, String(sourcePage));
      }

      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    async fetchSourcePage({ availability = null, sourcePage = 1, signal }) {
      const response = await fetch(
        this.getFetchUrl({ availability, sourcePage }),
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = parsed.getElementById(this.productsId);

      if (!incomingGrid) {
        throw new Error('Gridul de produse nu a fost găsit în răspuns.');
      }

      return Array.from(incomingGrid.children).map((card) => ({
        html: card.outerHTML,
        soldOut: card.classList.contains(SOLD_OUT_CLASS)
      }));
    }

    async fetchAvailabilitySlice(availability, start, count, signal) {
      if (count <= 0) return [];

      const firstSourcePage = Math.floor(start / this.pageSize) + 1;
      const offset = start % this.pageSize;
      const required = offset + count;
      const pagesNeeded = Math.ceil(required / this.pageSize);
      const requests = [];

      for (let index = 0; index < pagesNeeded; index += 1) {
        requests.push(
          this.fetchSourcePage({
            availability,
            sourcePage: firstSourcePage + index,
            signal
          })
        );
      }

      const pageCards = await Promise.all(requests);
      return pageCards
        .flat()
        .slice(offset, offset + count)
        .map((card) => card.html);
    }

    getPageSlices(virtualPage) {
      const start = (virtualPage - 1) * this.pageSize;
      const end = Math.min(start + this.pageSize, this.totalItems);

      const inStockStart = Math.min(start, this.inStockCount);
      const inStockEnd = Math.min(end, this.inStockCount);
      const inStockLength = Math.max(0, inStockEnd - inStockStart);

      const outStartGlobal = Math.max(start, this.inStockCount);
      const outEndGlobal = Math.max(outStartGlobal, end);
      const outOfStockStart = Math.max(0, outStartGlobal - this.inStockCount);
      const outOfStockLength = Math.max(0, outEndGlobal - outStartGlobal);

      return {
        inStockStart,
        inStockLength,
        outOfStockStart,
        outOfStockLength
      };
    }

    async loadViaAvailabilityBackend(virtualPage, signal) {
      const slices = this.getPageSlices(virtualPage);

      const [inStockCards, outOfStockCards] = await Promise.all([
        this.fetchAvailabilitySlice(
          '1',
          slices.inStockStart,
          slices.inStockLength,
          signal
        ),
        this.fetchAvailabilitySlice(
          '0',
          slices.outOfStockStart,
          slices.outOfStockLength,
          signal
        )
      ]);

      return [...inStockCards, ...outOfStockCards];
    }

    async buildAggregateCache(signal) {
      if (this.aggregateCache) return this.aggregateCache;

      const pageResults = new Array(this.nativePages);
      let nextPage = 1;
      const workerCount = Math.min(4, this.nativePages);

      const worker = async () => {
        while (nextPage <= this.nativePages) {
          const page = nextPage;
          nextPage += 1;
          pageResults[page - 1] = await this.fetchSourcePage({
            sourcePage: page,
            signal
          });
        }
      };

      await Promise.all(
        Array.from({ length: workerCount }, () => worker())
      );

      const allCards = pageResults.flat();
      const seen = new Set();
      const inStock = [];
      const soldOut = [];

      for (const card of allCards) {
        const match = card.html.match(/data-product-id="([^"']+)"/i);
        const productId = match?.[1] || card.html;

        if (seen.has(productId)) continue;
        seen.add(productId);

        if (card.soldOut) {
          soldOut.push(card.html);
        } else {
          inStock.push(card.html);
        }
      }

      this.aggregateCache = [...inStock, ...soldOut];
      return this.aggregateCache;
    }

    async loadViaAggregatePages(virtualPage, signal) {
      const sortedCards = await this.buildAggregateCache(signal);
      const start = (virtualPage - 1) * this.pageSize;
      return sortedCards.slice(start, start + this.pageSize);
    }

    setLoading(state) {
      this.loading = state;
      this.root.classList.toggle('miresmo-stock-pagination-loading', state);
      this.grid.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    async loadPage(virtualPage, options = {}) {
      const { scroll = false, updateHistory = false } = options;

      if (this.abortController) {
        this.abortController.abort();
      }

      const controller = new AbortController();
      this.abortController = controller;
      this.setLoading(true);

      try {
        const cards = this.useAvailabilityBackend
          ? await this.loadViaAvailabilityBackend(
              virtualPage,
              controller.signal
            )
          : await this.loadViaAggregatePages(
              virtualPage,
              controller.signal
            );

        if (!cards.length && this.totalItems > 0) {
          throw new Error('Nu au fost returnate produse pentru pagina cerută.');
        }

        this.grid.innerHTML = cards.join('');

        // The theme hides product cards inside hdt-reval-items until its
        // native reveal animation runs. Because these cards are injected
        // dynamically, replay the same lifecycle used by the theme's own
        // Load More component so the new cards never remain at opacity: 0.
        this.grid.dispatchEvent(
          new CustomEvent('products:update', { bubbles: true })
        );

        if (typeof this.grid.revealInstant === 'function') {
          this.grid.revealInstant();
        } else if (typeof this.grid.reveal === 'function') {
          this.grid.reveal();
        } else {
          this.grid
            .querySelectorAll('.hdt-card-product')
            .forEach((card) => {
              card.style.opacity = '1';
              card.style.transform = 'none';
            });
        }

        this.currentPage = virtualPage;

        if (updateHistory) {
          window.history.pushState({}, '', createUrl(virtualPage));
        }

        this.renderPagination();

        if (scroll) {
          const title = this.root.querySelector('.miresmo-collection-heading');
          const target = title || this.root;
          const offset = 96;
          const top = target.getBoundingClientRect().top + window.scrollY - offset;

          window.scrollTo({
            top: Math.max(0, top),
            behavior: 'smooth'
          });
        }

        document.dispatchEvent(
          new CustomEvent('miresmo:collection-page-changed', {
            detail: {
              page: virtualPage,
              mode: this.useAvailabilityBackend
                ? 'availability-filter'
                : 'aggregate-pages'
            }
          })
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Miresmo stock pagination]', error);

          // Safe fallback: keep Shopify's server-rendered page and native pager.
          this.nativePagination?.classList.remove('miresmo-native-pagination-hidden');
          this.mount.innerHTML = '';
        }
      } finally {
        if (this.abortController === controller) {
          this.setLoading(false);
        }
      }
    }

    renderPagination() {
      if (!this.mount) return;

      this.mount.innerHTML = '';

      if (this.totalPages <= 1) return;

      const wrapper = document.createElement('div');
      wrapper.className =
        'hdt-pagination-wrapper hdt-pagination-main-pages miresmo-stock-pagination';

      const nav = document.createElement('nav');
      nav.className = 'hdt-pagination';
      nav.setAttribute('aria-label', 'Paginare produse');

      const list = document.createElement('ul');
      list.className = 'hdt-pagination__list';

      if (this.currentPage > 1) {
        list.appendChild(
          buildArrow('Anteriorul', this.currentPage - 1, 'previous')
        );
      }

      for (const page of getPagesToDisplay(this.currentPage, this.totalPages)) {
        list.appendChild(buildPageItem(page, this.currentPage));
      }

      if (this.currentPage < this.totalPages) {
        list.appendChild(
          buildArrow('Următorul', this.currentPage + 1, 'next')
        );
      }

      nav.appendChild(list);
      wrapper.appendChild(nav);
      this.mount.appendChild(wrapper);
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.miresmoStockPagerInitialized === 'true') return;

    root.dataset.miresmoStockPagerInitialized = 'true';

    const pager = new MiresmoStockPager(root);
    pager.init();
    root.__miresmoStockPager = pager;
  }

  function scan() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function refreshCurrentPagerFromHistory() {
    const root = document.querySelector(ROOT_SELECTOR);
    const pager = root?.__miresmoStockPager;

    if (!pager || !pager.shouldRun()) return;

    const target = Math.min(
      Math.max(1, getVisibleVirtualPage()),
      Math.max(1, pager.totalPages)
    );

    pager.loadPage(target, {
      scroll: false,
      updateHistory: false
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  window.addEventListener('popstate', refreshCurrentPagerFromHistory);

  const observer = new MutationObserver((mutations) => {
    let shouldScan = false;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches?.(ROOT_SELECTOR) || node.querySelector?.(ROOT_SELECTOR)) {
          shouldScan = true;
          break;
        }
      }

      if (shouldScan) break;
    }

    if (shouldScan) window.requestAnimationFrame(scan);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
